// region Store
import {observe, React, _, pluralize, utils} from '@api'
import {AssortmentCardStore, CatalogItemsPanelContentProps, GroupByGroupStore, ProductsVirtualRowStore, VipLocalStore} from '@modules'
import {LOG_GLOBAL_CATALOG_LOAD, StoresInstance, ui} from '@stores'
import {action, autorun, computed, observable, reaction, runInAction, trace, when} from 'mobx'
import {useContext, useEffect} from 'react'

/**
 * Responsible for querying out 'rows' of data from the backend
 */
export class CatalogItemsCardViewStore extends VipLocalStore {
  constructor(stores: StoresInstance,public props: CatalogItemsPanelContentProps & {assortment: AssortmentCardStore, group: GroupByGroupStore}) {
    super(stores)

    if (props.group) {
      props.group.cardViewStore = this
    }
  }

  /** Changes to this cause an animation pass */
  @observable sharedId = 0
  @observable initialDataLoaded = false

  @observable everLoadedData = false

  @computed get maxAllowedTimesClicked() : number {
    const {itemsPerClick, props: {onlineItemCount}} = this
    return Math.max(1, Math.ceil(onlineItemCount / itemsPerClick))
  }
  /**
   * When we reset stores we don't lose our existing rendered items
   */
  @observable key: string = utils.guid()

  @computed get catalogItemsDict() {
    return _.keyBy(this.catalogItemsArray, v => v._id)
  }

  virtualRowStore: { [index: number]: ProductsVirtualRowStore } = {}

  @computed get hasMore() {
    const {timesClicked, itemsPerClick, props: {onlineItemCount}} = this
    return timesClicked * itemsPerClick < onlineItemCount
  }

  @computed get from() {
    const {timesClicked, itemsPerClick} = this
    return Math.max(1, (timesClicked - 1) * itemsPerClick)
  }

  @computed get to() {
    const {timesClicked, itemsPerClick, props: {onlineItemCount}} = this
    return Math.min(onlineItemCount, timesClicked * itemsPerClick)
  }

  @computed get groupNode() {
    return this.props.groupStore?.storeNode
  }

  @computed get showOfflineItemsOnly() {
    return this.props.groupStore?.showGroupOfflineItemsOnly
  }

  @computed get debugId() {
    const {stores: {storeId}, props: {groupStore}} = this

    return `[${storeId}]${groupStore ? ` - [${groupStore.queryKey}]` : ''}`
  }

  registerEffects = () => {
    const {debugId, vlp, catalog, catalog: {groupBy, groupBy: {isProductView}}, groupNode} = this

    const toDispose = [
      observe(this, 'itemsPerClick', ({oldValue}: any) => {
        this.previousItemsPerClick = oldValue
      }),
      autorun(() => {
        if (_.isNaN(this.timesClicked)) {
          this.timesClicked = 1
        }

        // Remove invalid virtual rows after we filter
        const {itemsPerClick, props: {onlineItemCount}} = this
        runInAction(() => {
          while (this.timesClicked * itemsPerClick > onlineItemCount + itemsPerClick) {
            this.timesClicked--
          }
        })
      }),
      reaction(() => {
        //trace()
        return _.cloneDeep(vlp.hierarchy.selected)
      }, () => {
        // Resetting catalog virtual rows to
        if (this.timesClicked != 1) {
          console.debug(`${debugId} - Reset loaded cards when vlp hierarchy changes`)
          this.timesClicked = 1
        }
      }),
      autorun(() => {
        if (!catalog.loadingCounts) {
          const {id, perspective, _sourceDynamic, refetchId, queryVariablesEs, itemCount} = catalog, {props: {queryVariables,
            onlineItemCount}, stores: {currentUser: {userSettings: {dev}}}} = this

          runInAction(async() => {
            LOG_GLOBAL_CATALOG_LOAD && catalog.isProductView && console.debug(`Reset card view virtualized rows`, this)
            this.key = utils.guid()
            this.loadingIndex = -1
            const {itemsPerClick, maxAllowedTimesClicked, virtualRowStore, timesClicked} = this,
                size = _.size(virtualRowStore)

            //Comment below if we don't want to reset the # of cards each time we move around the hierarchy
            //this.timesClicked = 1 // Causes YFTS-8620 as cards will disappear during refetch if we reset this

            for (var i = 0; i <= timesClicked && i < size; i++) {
              if (i < maxAllowedTimesClicked) {
                const virtualRow = virtualRowStore[i]
                if (virtualRow?.hasLoadedResult) {
                  if (i * itemsPerClick < onlineItemCount) {
                    await virtualRow?.runQuery({_sourceDynamic, queryVariables, refetchId})
                  }
                }
              }
            }

            // Don't reset times clicked when changing
//        store.timesClicked = Math.min(Math.ceil((props.onlineItemCount+ props.offlineItemCount) / store.itemsPerClick), store.timesClicked)
          })
        }
      }, {name: `Refetch existing virtual rows when view options, filters, etc change`})
    ]

    if (isProductView) {
      groupBy.catalogItemViewStore = this
    } else if (groupNode) {
      groupNode.productStore = this
    } else {
      toDispose.push(when(() => this.groupNode != null, () => {
        this.groupNode.productStore = this
      }))
    }

    return () => toDispose.forEach(d => d())
  }

  // Used for stack view only
  @observable showAfterEnoughTimeHasPassedWithoutSiblingsLoading = true

  @observable loadingIndex = 0

  @observable previousItemsPerClick

  @computed get isLoadingMore() {
    const {loadingIndex, initialDataLoaded} = this
    return loadingIndex >= 0 || !initialDataLoaded
  }

  @computed get loadingIndicatorText() {
    const {initialDataLoaded, catalog: {strings, catalogItemName}} = this

    if (!initialDataLoaded) {
      return catalogItemName === strings.emptyLabel ? '' : `Loading ${pluralize(catalogItemName)}...`
    }
  }

  @computed get itemsPerClick() {
    const {/*cardsPerRow, cardRowsPerScreen, */ props: {itemsPerClick}} = this,
      {
        ///catalog: {settings: {levelOfDetail}},
        stores: {config, currentUser: {userSettings: {dev}}}} = this

    if (!_.isNaN(itemsPerClick) && _.isNumber(itemsPerClick)) return itemsPerClick

    if (config.isTesting) return 10
    else {
      return dev?.virtualizedCatalog ? 500 : ui.settings.defaults.catalog.cardsPerVirtualRow //  If we change this we change the query which will then unmount 40 of our items! levelOfDetail === 1 ? 10 : 50
    }
  }

  //todo move into linked group node so we can avoid resetting to 1 when we collapse and reexpand a group
  /**
   * Min should be 1 (not 0!)
   */
  @observable timesClicked : number = 1

  //@observable availableSize = {width: 0, height: 0}

  @computed get maxLoaded() {
    const {timesClicked, props: {onlineItemCount}, itemsPerClick} = this

    return Math.min(onlineItemCount, timesClicked * itemsPerClick)
  }

  @action onPageBack = (e: React.MouseEvent) => {
    if (this.timesClicked > 1) {
      this.timesClicked--
      this.loadingIndex = this.timesClicked - 1
    }

    // Prevent the clearSelection code from triggering above us in the DOM
    e.stopPropagation()
  }

  @action onPageForward = (e?: React.MouseEvent) => {
    this.timesClicked++
    this.loadingIndex = this.timesClicked - 1

    // Prevent the clearSelection code from triggering above us in the DOM
    e?.stopPropagation()
  }

  /**
   * Used for shift click - ordered list of product data
   *
   * When a perspective is set, each individual item may be transformed into many objects (1 mpv -> multiple colorways)
   */
  @computed get onlineCatalogItemsArray(): any[] {
    const {virtualRows, catalog: {perspective}} = this
    return virtualRows.flatMap(v => (v?.itemWrappers ?? []).map(
            d => {
              const v = !perspective?.field ? d : _.get(d, perspective.field)
              return !perspective?.field
                     ? v
                     : !_.isArray(v)
                       ? {__parent: d, ...v}
                       : _.flatMap(v, arrVal => ({__parent: d, ...arrVal}))
            }
          )).filter(_.identity)
  }

  @computed get virtualRows() {
    return _.range(0, this.timesClicked).map(i => this.virtualRowStore[i]).filter(_.identity)
  }

  @computed get catalogItemsArray(): any[] {
    const {onlineCatalogItemsArray, offlineItems} = this
    return [
      ...offlineItems,
      ...onlineCatalogItemsArray
    ]
  }

  @computed get totalCount() { return this.props.onlineItemCount + this.props.offlineItemCount}

  @computed get catalogItemIdsArray(): string[] {
    const {catalog: {idFor, settings: {perItem, showOnlyInlineCatalogItems, showOnlyItemsWithChanges, autoSaveInlineChanges, levelOfDetail}}} = this

    let ids = this.catalogItemsArray.map(v => idFor(v))
    if (levelOfDetail === 0 && showOnlyInlineCatalogItems) {
      ids = ids.filter(id => perItem[id]?.lod === 1)
    }

    if (showOnlyItemsWithChanges && !autoSaveInlineChanges) {
      ids = ids.filter(id => perItem[id]?.unsavedChanges)
    }

    return ids
  }

  @computed get itemPositionByID() {
    return this.catalogItemIdsArray.reduce((acc, v, i) => _.assign(acc, {[v]: i}), {})
  }

  @action onClickCard = (e, id) => {
    const {catalog: {selection: {onClickItem}}} = this,
      {catalogItemIdsArray, groupNode} = this
    onClickItem(e, id, catalogItemIdsArray, groupNode?.store?.settings?.stackView)
  }

  @computed get offlineItems() {
    const {props: {groupStore}, catalog: {filteredOfflineItems}} = this
    if (groupStore) {
      return groupStore.offlineItems ?? []
    }
    //return !groupNode ? items : items.filter(i => groupNode.store.pathIncludingSelf)

    return filteredOfflineItems //_.chain(catalog.offlineItemsByIndex[catalog.index]).values().orderBy('creationDate', 'desc').value() ?? []
  }

  @action reset = () => {
    console.debug(`Resetting catalog panel`)
    this.timesClicked = 1
    this.initialDataLoaded = false
  }
}

//region Context
export const CatalogItemsCardViewStoreContext = React.createContext<CatalogItemsCardViewStore>(undefined)
export const useCatalogItemsCardViewStore = () => useContext(CatalogItemsCardViewStoreContext)
//endregion
//endregion


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_CatalogItems/CardView/LoadMoreProductCatalog/CatalogItemsCardViewStore.ts